import {ControllerFlowAPI} from '@wix/yoshi-flow-editor'
import {Reservation} from '@wix/ambassador-table-reservations-v1-reservation/types'
import {Experience} from '@wix/ambassador-table-reservations-v1-experience/types'

import {experiencesService} from '../services/experiencesService'

import {
  APP_DEF_ID,
  AppPage,
  CHECKOUT_PAGE_REFERRER,
  ECOM_APP_DEF_ID,
  EXPERIMENTS,
  RESERVATION_DETAILS_REFERRER,
} from './constants'
import {getUrlParamsFromObject} from './queryParams'

type IsoDateString = string

type ReservationsParams = {
  reservationLocationId?: string
  startDate?: IsoDateString
  partySize?: string
  areaId?: string
  isTemplateView?: string
}

type ReservationDetailsParams = {
  experienceId?: string
  isTemplateView?: string
  utmSource?: string
  utmMedium?: string
} & (
  | {
      reservationId: string
      areaId?: string
    }
  | {
      startDate: string
      partySize: string
      reservationLocationId: string
      areaId?: string
    }
)

type ReservationConfirmationParams = {
  reservationId: string
  orderId?: string
  experienceId?: string
  isTemplateView?: string
  referrer?: string
}

type ExperienceDetailsParams = {
  experienceId?: string
  referrer?: string
  isTemplateView?: string
}

type AppPageToParamsMap = {
  [AppPage.Reservations]?: ReservationsParams
  [AppPage.ReservationDetails]: ReservationDetailsParams
  [AppPage.ReservationConfirmation]: ReservationConfirmationParams
  [AppPage.ExperienceDetails]: ExperienceDetailsParams
  [AppPage.ExperiencesList]: {}
}

type AppPageToParams<T extends AppPage> = AppPageToParamsMap[T]

export async function getRelativeUrlWithParams<T extends AppPage>(
  page: T,
  params: AppPageToParams<T>,
  flowAPI: ControllerFlowAPI,
) {
  const {relativeUrl} = await flowAPI.controllerConfig.wixCodeApi.site.getSectionUrl({
    sectionId: page,
    appDefinitionId: APP_DEF_ID,
  })

  const preparedParams = getUrlParamsFromObject(params || {})

  return `${relativeUrl}?${preparedParams}`
}

export async function goToPage<T extends AppPage>(
  page: T,
  params: AppPageToParams<T>,
  flowAPI: ControllerFlowAPI,
) {
  const url = await getRelativeUrlWithParams(page, params, flowAPI)

  flowAPI.controllerConfig.wixCodeApi.location.to?.(url)
}

type ReservationDataOptional = Pick<
  Exclude<Reservation['details'], undefined>,
  'reservationLocationId' | 'startDate' | 'partySize' | 'areaId'
>

type ReservationData = {
  reservationLocationId: NonNullable<ReservationDataOptional['reservationLocationId']>
  startDate: NonNullable<ReservationDataOptional['startDate']>
  partySize: NonNullable<ReservationDataOptional['partySize']>
  areaId?: ReservationDataOptional['areaId']
}

export const goToNewReservation = ({
  flowAPI,
  reservationData,
  isTemplateView,
}: {
  flowAPI: ControllerFlowAPI
  timeZone?: string | null
  reservationData?: ReservationDataOptional
  isTemplateView?: boolean
}) =>
  goToPage(
    AppPage.Reservations,
    {
      reservationLocationId: reservationData?.reservationLocationId ?? undefined,
      startDate: reservationData?.startDate?.toISOString(),
      partySize: reservationData?.partySize?.toString(),
      areaId: reservationData?.areaId ?? undefined,
      ...(isTemplateView ? {isTemplateView: 'true'} : {}),
    },
    flowAPI,
  )

export const goToReservationDetails = ({
  flowAPI,
  reservationData,
  reservationId,
  isTemplateView,
  experienceId,
  areaId,
}: {
  flowAPI: ControllerFlowAPI
  timeZone?: string | null
  isTemplateView?: boolean
  reservationId?: string | null
  reservationData?: ReservationData
  experienceId?: string
  areaId?: ReservationDataOptional['areaId']
}) => {
  const urlParams = flowAPI.controllerConfig?.wixCodeApi?.location?.query ?? {}
  const utmSource = urlParams.utm_source
  const utmMedium = urlParams.utm_medium
  const utmParams = utmSource && utmMedium ? {utmSource, utmMedium} : {}

  if (reservationId) {
    const params: AppPageToParams<AppPage.ReservationDetails> = {
      reservationId,
      areaId: areaId ?? undefined,
      ...(isTemplateView ? {isTemplateView: 'true'} : {}),
      ...utmParams,
    }

    return goToPage(AppPage.ReservationDetails, params, flowAPI)
  }

  if (reservationData) {
    const params: AppPageToParams<AppPage.ReservationDetails> = {
      reservationLocationId: reservationData.reservationLocationId,
      startDate: reservationData.startDate.toISOString(),
      partySize: reservationData.partySize.toString(),
      areaId: reservationData.areaId ?? undefined,
      ...(experienceId ? {experienceId} : {}),
      ...(isTemplateView ? {isTemplateView: 'true'} : {}),
      ...utmParams,
    }

    return goToPage(AppPage.ReservationDetails, params, flowAPI)
  }
}

export const goToReservationConfirmation = ({
  flowAPI,
  reservationId,
  orderId,
  isTemplateView,
}: {
  flowAPI: ControllerFlowAPI
  reservationId?: string | null
  isTemplateView?: boolean
  orderId?: string
}) => {
  if (reservationId) {
    return goToPage(
      AppPage.ReservationConfirmation,
      {
        reservationId,
        orderId,
        referrer: RESERVATION_DETAILS_REFERRER,
        ...(isTemplateView ? {isTemplateView: 'true'} : {}),
      },
      flowAPI,
    )
  }
}

export const goToExperienceDetails = async ({
  flowAPI,
  experience,
  experienceId,
  referrer,
  isTemplateView,
}: {
  flowAPI: ControllerFlowAPI
  experience?: Experience
  experienceId?: string | null
  referrer: string
  isTemplateView?: boolean
}) => {
  const id = experience?.id ?? experienceId

  if (!id) {
    return
  }

  const slug = (experience ?? (await experiencesService.getExperience(flowAPI, id)))?.slug

  const params = {
    referrer,
    ...(isTemplateView ? {isTemplateView: 'true'} : {}),
  }

  const isExperiencesSeoIntegrationEnabled = flowAPI.experiments.enabled(
    EXPERIMENTS.isExperiencesSeoIntegrationEnabled,
  )

  if (slug && isExperiencesSeoIntegrationEnabled) {
    const {relativeUrl} = await flowAPI.controllerConfig.wixCodeApi.site.getSectionUrl({
      sectionId: AppPage.ExperienceDetails,
      appDefinitionId: APP_DEF_ID,
    })
    const query = getUrlParamsFromObject(params)
    const url = `${relativeUrl}/${slug}${query ? `?${query}` : ''}`

    flowAPI.controllerConfig.wixCodeApi.location.to?.(url)
  } else {
    return goToPage(AppPage.ExperienceDetails, {...params, experienceId: id}, flowAPI)
  }
}

export const navigateToCheckoutPage = async (
  flowAPI: ControllerFlowAPI,
  checkoutId: string,
  reservationId: string,
) => {
  const wixCodeApi = flowAPI.controllerConfig.wixCodeApi
  const ecomApi = await wixCodeApi.site.getPublicAPI(ECOM_APP_DEF_ID)

  const baseUrl = wixCodeApi.location.baseUrl

  const relativeUrlWithParams = await getRelativeUrlWithParams(
    AppPage.ReservationConfirmation,
    {orderId: '{orderId}', reservationId, referrer: CHECKOUT_PAGE_REFERRER},
    flowAPI,
  )

  const query = flowAPI.controllerConfig?.wixCodeApi?.location?.query ?? {}

  // ecom converts successUrl to json, so we need to replace double quotes
  // with url encoded %22 to avoid JSON parsing errors
  for (const key in query) {
    if (query.hasOwnProperty(key)) {
      query[key] = query[key].replaceAll('"', '%22')
    }
  }

  const queryParams = getUrlParamsFromObject(query)

  // successUrl is stored in checkout page query params
  // so we need to encode "&" to "%26" to avoid URL parsing errors
  const successUrl = `${baseUrl}${relativeUrlWithParams}%26${queryParams}`.replaceAll('&', '%26')

  return ecomApi.navigate.toCheckout({
    checkoutId,
    disableContinueShopping: true,
    successUrl,
  })
}

export const openBmLocationSettingsPage = (metaSiteId: string) =>
  window.open(`https://manage.wix.com/dashboard/${metaSiteId}/settings/business-info`)
